/**
 * Helpers pra ordenação de receitas por afinidade cultural (cuisine_origin).
 *
 * Em vez de FILTRAR receitas por `market`, o app agora mostra TODAS as receitas
 * no idioma do usuário, e ordena pelas que combinam com a preferência cultural
 * do usuário (market do perfil).
 */

/**
 * Mapeia o market do usuário (mx/us) para a cuisine_origin correspondente.
 * Usuário do MX vê receitas mexicanas primeiro; US vê americanas primeiro.
 */
export function preferredCuisineFromMarket(market: string | null | undefined): string {
  switch ((market || "").toLowerCase()) {
    case "mx":
      return "mexican";
    case "us":
      return "american";
    case "de":
      return "european";
    default:
      return "universal";
  }
}

/**
 * Ordena uma lista de receitas pondo as do cuisine_origin preferido na frente.
 *
 * Ordem:
 *   1. Receitas que combinam com a preferência cultural do usuário
 *   2. Receitas "universal" (qualquer pessoa faz)
 *   3. Receitas "seasonal" (Natal, Páscoa, etc)
 *   4. Resto
 *   — dentro de cada grupo, preserva o sort_order original.
 */
export function sortByCuisineAffinity<T extends { cuisine_origin?: string | null; sort_order?: number | null }>(
  recipes: T[],
  preferredMarket: string | null | undefined,
): T[] {
  if (!recipes || recipes.length === 0) return recipes;
  const preferred = preferredCuisineFromMarket(preferredMarket);

  const tierOf = (r: T): number => {
    const co = (r.cuisine_origin || "universal").toLowerCase();
    if (co === preferred) return 0;
    if (co === "universal") return 1;
    if (co === "seasonal") return 2;
    return 3;
  };

  return [...recipes].sort((a, b) => {
    const ta = tierOf(a);
    const tb = tierOf(b);
    if (ta !== tb) return ta - tb;
    const sa = a.sort_order ?? 999_999;
    const sb = b.sort_order ?? 999_999;
    return sa - sb;
  });
}

/** Lista oficial de valores de cuisine_origin aceitos pela aplicação. */
export const CUISINE_ORIGINS = [
  "mexican",
  "american",
  "latin",
  "mediterranean",
  "italian",
  "asian",
  "european",
  "seasonal",
  "universal",
] as const;

export type CuisineOrigin = (typeof CUISINE_ORIGINS)[number];

/** Label legível em ES, EN e DE pra exibir no admin / filtros. */
export const CUISINE_ORIGIN_LABELS: Record<CuisineOrigin, { es: string; en: string; de: string }> = {
  mexican: { es: "Mexicana", en: "Mexican", de: "Mexikanisch" },
  american: { es: "Estadounidense", en: "American", de: "Amerikanisch" },
  latin: { es: "Latinoamericana", en: "Latin American", de: "Lateinamerikanisch" },
  mediterranean: { es: "Mediterránea", en: "Mediterranean", de: "Mediterran" },
  italian: { es: "Italiana", en: "Italian", de: "Italienisch" },
  asian: { es: "Asiática", en: "Asian", de: "Asiatisch" },
  european: { es: "Europea", en: "European", de: "Europäisch" },
  seasonal: { es: "De temporada", en: "Seasonal", de: "Saisonal" },
  universal: { es: "Universal", en: "Universal", de: "Universal" },
};
